import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchCurrentUser } from '../redux/CurrentUserReducer/currentUserSlice';
import { setAuthenticated } from '../redux/AuthReducer/authSlice';
import { tokenService } from '../services/tokenService';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import SessionTimeoutManager from './SessionTimeoutManager';

interface AppInitializerProps {
    children: React.ReactNode;
}

interface LoadingStateProps {
    message: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({ message }) => (
    <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 2
    }}>
        <CircularProgress size={60} />
        <Typography variant="h6">{message}</Typography>
    </Box>
);

/**
 * Component that initializes the application by fetching the current user's profile
 * This runs once when the app loads and handles token persistence
 */
const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const { user, loading/*, error */ } = useAppSelector((state) => state.currentUser);
    const location = useLocation();
    const [isInitializing, setIsInitializing] = useState(true);
    const hasInitialized = useRef(false);

    // Public routes that don't require authentication
    const publicRoutes = ['/login', '/registration', '/forgot-password', '/site-select'];
    const isPublicRoute = publicRoutes.includes(location.pathname);

    useEffect(() => {
        const initializeAuth = async () => {
            // Prevent re-initialization
            if (hasInitialized.current) {
                return;
            }
            hasInitialized.current = true;

            console.log('🔄 AppInitializer: Starting initialization');
            const hasToken = tokenService.hasToken();
            console.log('🔑 Token exists:', hasToken);

            // If on public route, skip authentication
            if (isPublicRoute) {
                console.log('📄 Public route detected, skipping auth check');
                setIsInitializing(false);
                return;
            }

            // If we have a token but no user data, fetch the user
            if (hasToken) {
                console.log('✅ Token found, setting authenticated state');
                dispatch(setAuthenticated(true));

                // Only fetch user if we don't already have user data
                if (!user && !loading) {
                    console.log('👤 Fetching user profile...');
                    try {
                        await dispatch(fetchCurrentUser()).unwrap();
                        console.log('✅ User profile fetched successfully');
                    } catch (error) {
                        console.error('❌ Failed to fetch user profile:', error);
                        // Token might be invalid, clear it
                        tokenService.removeToken();
                        dispatch(setAuthenticated(false));
                    }
                }
            } else {
                console.log('❌ No token found, clearing authentication');
                dispatch(setAuthenticated(false));
            }

            setIsInitializing(false);
        };

        initializeAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update authentication when user data changes
    useEffect(() => {
        if (user && tokenService.hasToken()) {
            dispatch(setAuthenticated(true));
        }
    }, [user, dispatch]);

    // Show loading state while initializing (only for protected routes)
    if (!isPublicRoute && isInitializing) {
        return <LoadingState message="Loading..." />;
    }

    // Show loading state while fetching user data
    if (!isPublicRoute && loading && !user && tokenService.hasToken()) {
        return <LoadingState message="Loading user profile..." />;
    }

    // Render children
    return (
        <>
            <SessionTimeoutManager />
            {children}
        </>
    );
};

export default AppInitializer;